import { MouseEvent, PureComponent, ReactNode } from 'react';
import { Maybe } from '../../types';

type MouseEventHandler = (e: MouseEvent<HTMLElement>) => void;

interface HoverRenderProps {
  hover: boolean;
  hoveredBefore: boolean;
  onMouseEnter: MouseEventHandler;
  onMouseLeave: MouseEventHandler;
}

interface HoverProps {
  render: (props: HoverRenderProps) => ReactNode;
  timeout: number;
}

interface HoverState {
  hover: boolean;
  hoveredBefore: boolean;
}

export class Hover extends PureComponent<HoverProps, HoverState> {
  public state = {
    hover: false,
    hoveredBefore: false
  };

  public timeoutId: Maybe<NodeJS.Timeout> = null;

  public clearTimeout = () => {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  };

  public onMouseEnter: MouseEventHandler = e => {
    this.clearTimeout();
    this.setState({
      hover: true,
      hoveredBefore: true
    });
  };

  public onMouseLeave: MouseEventHandler = e => {
    this.timeoutId = setTimeout(() => {
      this.setState({
        hover: false
      });
    }, this.props.timeout);
  };

  public unsetHovering = () => {
    this.clearTimeout();
    this.setState({
      hover: false
    });
  };

  public componentDidUpdate() {
    this.state.hover && triggerHover(this);
  }

  public componentDidMount() {
    addHover(this);
  }

  public componentWillUnmount() {
    removeHover(this);
  }

  public render() {
    const { hover, hoveredBefore } = this.state;
    const { render } = this.props;
    const children = render({
      hover,
      hoveredBefore,
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave
    });
    return children;
  }
}

const hovers: Set<Hover> = new Set();

const addHover = (hover: Hover) => hovers.add(hover);

const removeHover = (hover: Hover) => hovers.delete(hover);

const triggerHover = (hover: Hover) => {
  hovers.forEach(h => {
    if (h !== hover) {
      h.unsetHovering();
    }
  });
};
